import type {
  CustomComponents,
  HastHeading,
  IndexableElements,
  RMDXModule,
  TocList,
  TocListItem,
  Variables,
} from '../../types';
import type { Element, Root } from 'hast';
import type { MdxjsEsm } from 'mdast-util-mdxjs-esm';
import type { Transformer } from 'unified';

import { valueToEstree } from 'estree-util-value-to-estree';
import { h } from 'hastscript';

import { mdx, plain } from '../../lib';
import { STANDARD_HTML_TAGS } from '../../utils/common-html-words';
import { hasNamedExport } from '../utils';

const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const isHeadingTag = (tag?: string) => (tag ? HEADING_TAGS.includes(tag) : false);

const isStandardHtmlElement = (node: Element): boolean => STANDARD_HTML_TAGS.has(node.tagName.toLowerCase());

interface Options {
  components?: CustomComponents;
}

/** Extract all heading elements (h1-h6) from a HAST tree, excluding those inside custom components
 * Used to generate toc for mdxish
 */
export function extractToc(tree: Root, components: CustomComponents = {}): HastHeading[] {
  const headings: HastHeading[] = [];
  const componentsByTag = new Map<string, RMDXModule>(
    Object.entries(components).map(([tag, mod]) => [tag.toLowerCase(), mod]),
  );

  // Recursively walk component TOCs so headings declared inside nested custom components are found.
  const collectComponentHeadings = (tag: string, seen: Set<string>): HastHeading[] => {
    const component = componentsByTag.get(tag);
    if (!component?.toc || seen.has(tag)) return [];

    seen.add(tag);
    const collected = (component.toc as IndexableElements[]).flatMap(entry => {
      if (entry.type === 'element' && isHeadingTag(entry.tagName)) {
        return [entry as HastHeading];
      }
      if (entry.type === 'mdxJsxFlowElement' && typeof entry.name === 'string') {
        return collectComponentHeadings(entry.name.toLowerCase(), seen);
      }
      return [];
    });
    seen.delete(tag);

    return collected;
  };

  // Depth-first traversal over the HAST tree, collecting headings while skipping custom component bodies.
  const traverse = (node: Root | Root['children'][number]): void => {
    if (node.type === 'element') {
      const tag = node.tagName.toLowerCase();

      if (isHeadingTag(node.tagName)) {
        headings.push(node as HastHeading);
      }

      if (componentsByTag.has(tag)) {
        headings.push(...collectComponentHeadings(tag, new Set()));
      }

      // Only traverse into standard HTML elements, skip custom components (like Callout)
      if (isStandardHtmlElement(node) && node.children) {
        node.children.forEach(traverse);
      }
    } else if ('children' in node && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  };

  traverse(tree);
  return headings;
}

/** A rehype plugin to generate a flat list of top-level headings or jsx flow elements. */
export const rehypeToc = ({ components = {} }: Options): Transformer<Root, Root> => {
  return (tree: Root): void => {
    if (hasNamedExport(tree, 'toc')) return;

    const headings = tree.children.filter(
      child =>
        (child.type === 'element' && HEADING_TAGS.includes(child.tagName)) ||
        (child.type === 'mdxJsxFlowElement' && child.name in components),
    ) as IndexableElements[];

    tree.children.unshift({
      type: 'mdxjsEsm',
      data: {
        estree: {
          type: 'Program',
          sourceType: 'module',
          body: [
            {
              type: 'ExportNamedDeclaration',
              source: null,
              specifiers: [],
              declaration: {
                type: 'VariableDeclaration',
                kind: 'const',
                declarations: [
                  {
                    type: 'VariableDeclarator',
                    id: { type: 'Identifier', name: 'toc' },
                    init: valueToEstree(headings),
                  },
                ],
              },
            },
          ],
        },
      },
    } as MdxjsEsm);
  };
};

const MAX_DEPTH = 2;

/**
 * Get the depth of a heading element based on its tag name.
 *
 * ⚠️ Be extra defensive to non-heading elements somehow making it here. This
 * should not happen, but if it does, we avoid breaking TOC depth calculations
 * by returning `Infinity`, thus removing them from depth considerations.
 * @link https://linear.app/readme-io/issue/CX-2543/tabapay-toc-does-not-respect-headers-nesting
 *
 * @example
 * getDepth({ tagName: 'h1' }) // 1
 * getDepth({ tagName: 'h2' }) // 2
 */
const getDepth = (el: HastHeading) => {
  if (!el.tagName) return Infinity;
  return parseInt(el.tagName?.match(/^h(\d)/)[1], 10);
};

/**
 * Flatten Variables into a simple key-value map for static resolution.
 * Merges user values with defaults (user values take precedence).
 */
const flattenVariables = (variables?: Variables): Record<string, string> => {
  if (!variables) return {};
  return {
    ...variables.user,
    ...Object.fromEntries(
      (variables.defaults || []).filter(d => !(d.name in variables.user)).map(d => [d.name, d.default]),
    ),
  };
};

/*
 * `tocToHast` consumes the list generated by `rehypeToc` and produces a hast
 * of nested lists to be rendered as a table of contents.
 *
 * @param headings - The list of heading elements to render
 * @param variables - Optional user variables for resolving Variable nodes in headings
 */
export const tocToHast = (headings: HastHeading[] = [], variables?: Variables): TocList => {
  const flatVars = flattenVariables(variables);
  const headingDepths = headings.map(getDepth);
  const min = Math.min(...headingDepths);
  const ast = h('ul') as TocList;
  const stack: TocList[] = [ast];

  headings.forEach(heading => {
    const depth = getDepth(heading) - min + 1;
    if (depth > MAX_DEPTH) return;

    while (stack.length < depth) {
      const ul = h('ul') as TocList;

      stack[stack.length - 1].children.push(h('li', null, ul) as TocListItem);
      stack.push(ul);
    }

    while (stack.length > depth) {
      stack.pop();
    }

    if (heading.properties) {
      const content = plain({ type: 'root', children: heading.children }, { separator: '', variables: flatVars }) as string;

      stack[stack.length - 1].children.push(
        h('li', null, h('a', { href: `#${heading.properties.id}` }, content)) as TocListItem,
      );
    }
  });

  return ast;
};

/*
 * `tocHastToMdx` is a utility for combining `TocList`s of a root document and
 * any child components it may have. Once combined it will generate a markdown
 * doc representing a table of contents.
 *
 * @param toc - The list of TOC elements
 * @param components - Custom components that may contain headings
 * @param variables - Optional user variables for resolving Variable nodes in headings
 */
export const tocHastToMdx = (
  toc: IndexableElements[] | undefined,
  components: Record<string, RMDXModule['toc']>,
  variables?: Variables,
) => {
  if (typeof toc === 'undefined') return '';

  const injected = toc.flatMap(node => {
    if (node.type === 'mdxJsxFlowElement') {
      return components[node.name] || [];
    }
    return node;
  });

  const tocHast = tocToHast(injected as HastHeading[], variables);
  return mdx({ type: 'root', children: [tocHast] }, { hast: true });
};
